import React, { useEffect } from "react";
import { Check, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useStartFlow } from "@/lib/useStart";
import { track, EVENTS } from "@/lib/analytics";

export const PLAN_DETAILS = {
  monthly: {
    id: "monthly",
    name: "MONTHLY",
    price: "₹1,999",
    period: "/month",
    tagline: "Full access, billed monthly. Cancel anytime.",
    cta: "Start Monthly",
    features: [
      "AI Exam Coach that knows your preparation",
      "Personalized daily plan",
      "Adaptive practice",
      "AI mock analysis (upload & auto-read reports)",
      "Mistake Bank",
      "Performance tracking & analytics",
      "AI doubt support",
      "Parent progress view",
    ],
  },
  annual: {
    id: "annual",
    name: "ANNUAL",
    price: "₹19,999",
    period: "/year",
    tagline: "≈ ₹1,667/month · Save ₹3,989 vs paying monthly for 12 months.",
    cta: "Choose Annual",
    popular: true,
    features: [
      "Everything in Monthly",
      "Advanced performance analytics",
      "Advanced mock analysis",
      "Long-term preparation roadmap",
      "Advanced AI interventions",
      "Priority support",
      "Success Guarantee eligibility, subject to terms",
    ],
  },
};

export const PricingCard = ({ plan, onChoose }) => {
  const popular = plan.popular;
  return (
    <div
      className={`relative flex flex-col rounded-3xl border p-8 transition-all duration-300 ${
        popular
          ? "border-amber-400/50 bg-gradient-to-b from-amber-500/[0.08] to-transparent md:scale-105 glow-cyan z-10"
          : "border-border bg-card hover:-translate-y-1"
      }`}
      data-testid={`pricing-card-${plan.id}`}
    >
      {popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-400 to-red-600 px-4 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-lg">
          <Sparkles className="h-3 w-3" /> Most Popular
        </span>
      )}
      <p className={`text-xs font-bold uppercase tracking-[0.2em] ${popular ? "text-amber-500 dark:text-amber-300" : "text-muted-foreground"}`}>
        {plan.name}
      </p>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-display text-4xl font-bold tracking-tight">{plan.price}</span>
        <span className="text-sm text-muted-foreground">{plan.period}</span>
      </div>
      <p className="mt-3 text-sm text-muted-foreground leading-relaxed min-h-[40px]">{plan.tagline}</p>

      <button
        onClick={() => onChoose(plan.id)}
        className={`mt-6 w-full rounded-full font-medium px-6 py-3.5 transition-all ${
          popular
            ? "bg-gradient-to-r from-amber-400 to-red-600 text-white hover:scale-105 shadow-[0_0_25px_rgba(250,204,21,0.35)]"
            : "bg-foreground text-background hover:scale-[1.03]"
        }`}
        data-testid={`pricing-cta-${plan.id}`}
      >
        {plan.cta}
      </button>

      <ul className="mt-8 space-y-3">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm">
            <Check className={`h-4 w-4 shrink-0 mt-0.5 ${popular ? "text-amber-500 dark:text-amber-300" : "text-primary"}`} />
            <span className="text-muted-foreground">{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const PricingSection = () => {
  const start = useStartFlow();

  useEffect(() => {
    track(EVENTS.PRICING_VIEW);
    // Fire the pricing-view event once on mount; track/EVENTS are stable module imports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section id="pricing" className="bg-background py-24 md:py-32 scroll-mt-16" data-testid="pricing-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">Pricing</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Choose how seriously you want to prepare.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground">
            Go month-to-month, or commit for a year and save. Either way, you get the entire system.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto items-center">
          <Reveal><PricingCard plan={PLAN_DETAILS.monthly} onChoose={(p) => start("pricing", p)} /></Reveal>
          <Reveal delay={0.08}><PricingCard plan={PLAN_DETAILS.annual} onChoose={(p) => start("pricing", p)} /></Reveal>
        </div>

        <p className="mt-10 text-center text-sm text-muted-foreground">
          Secure payment via Razorpay · Cancel anytime · Success Guarantee eligibility, subject to terms
        </p>
      </div>
    </section>
  );
};
